import React, { useState, useEffect, useCallback } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { getUserCharacter } from '../graphql/queries';
import { createUserCharacter, updateUserCharacter } from '../graphql/mutations';
import { CHARACTERS, getCharacterById } from '../constants/characters';

const client = generateClient();

// ============================================================
// SNSアイコン
// ============================================================
const YoutubeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const TwitchIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// ============================================================
// メインコンポーネント
// ============================================================
export default function CharSelect() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentCharId, setCurrentCharId] = useState(null);
  const [userRecordId, setUserRecordId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const selected = CHARACTERS[displayIndex];

  useEffect(() => {
    async function init() {
      try {
        const attrs = await fetchUserAttributes();
        const uid = attrs.sub;
        setUserId(uid);
        const result = await client.graphql({
          query: getUserCharacter,
          variables: { id: uid },
        });
        const record = result.data.getUserCharacter;
        if (record) {
          setCurrentCharId(record.characterId);
          setUserRecordId(record.id);
          const idx = CHARACTERS.findIndex(c => c.id === record.characterId);
          const safeIdx = idx >= 0 ? idx : 0;
          setSelectedIndex(safeIdx); setDisplayIndex(safeIdx);
        }
      } catch (err) {
        console.error('初期化エラー:', err);
      }
    }
    init();
  }, []);

  const handleSelect = useCallback((index) => {
    if (transitioning || index === selectedIndex) return;
    setTransitioning(true);
    setVisible(false);
    setTimeout(() => {
      setDisplayIndex(index);
      setSelectedIndex(index);
      setVisible(true);
      setTransitioning(false);
    }, 380);
  }, [transitioning, selectedIndex]);

  const handleConfirm = async () => {
    if (saving || !userId) return;
    setSaving(true);
    setSaveMessage('');
    try {
      const charId = CHARACTERS[selectedIndex].id;
      if (userRecordId) {
        await client.graphql({ query: updateUserCharacter, variables: { input: { id: userRecordId, characterId: charId } } });
      } else {
        const result = await client.graphql({ query: createUserCharacter, variables: { input: { id: userId, characterId: charId } } });
        setUserRecordId(result.data.createUserCharacter.id);
      }
      setCurrentCharId(charId);
      setSaveMessage('キャラクターを変更しました！');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('保存エラー:', err);
      setSaveMessage('保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={{ ...S.bgOverlay, background: `radial-gradient(ellipse at 65% 40%, ${selected.accentColor}44 0%, transparent 65%)`, transition: 'background 0.6s ease' }} />

      {/* 立ち絵：画面全体に表示 */}
      <div style={{ ...S.standImageWrap, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)', transition: 'opacity 0.38s ease, transform 0.38s ease' }}>
        <img src={selected.standImage} alt={selected.name} style={S.standImage} onError={e => { e.target.style.opacity = '0'; }} />
      </div>

      {/* キャッチコピー縦書き：左上 */}
      <div style={{ ...S.catchcopy, borderLeft: `3px solid ${selected.accentColor}`, opacity: visible ? 1 : 0, transition: 'opacity 0.38s ease' }}>
        {selected.catchcopy}
      </div>

      {/* 情報エリア：右下に固定 */}
      <div style={S.infoArea}>
        <div style={{ ...S.charName, opacity: visible ? 1 : 0, transition: 'opacity 0.38s ease' }}>
          <span style={{ color: selected.accentColor }}>■</span>
          {selected.name}
          {currentCharId === selected.id && (
            <span style={{ ...S.activeBadge, background: selected.accentColor }}>使用中</span>
          )}
        </div>

        <div style={{ ...S.descBox, opacity: visible ? 1 : 0, transition: 'opacity 0.38s ease 0.06s' }}>
          <div style={{ ...S.descLabel, color: selected.accentColor }}>CHARACTER</div>
          <p style={S.descText}>{selected.description}</p>
        </div>

        <div style={{ ...S.snsRow, opacity: visible ? 1 : 0, transition: 'opacity 0.38s ease 0.14s' }}>
          {selected.youtube && (
            <a href={selected.youtube} target="_blank" rel="noopener noreferrer" style={{ ...S.snsBtn, background: '#FF0000' }}>
              <YoutubeIcon />
            </a>
          )}
          {selected.twitch && (
            <a href={selected.twitch} target="_blank" rel="noopener noreferrer" style={{ ...S.snsBtn, background: '#9146FF' }}>
              <TwitchIcon />
            </a>
          )}
          {selected.twitter && (
            <a href={selected.twitter} target="_blank" rel="noopener noreferrer" style={{ ...S.snsBtn, background: '#111' }}>
              <TwitterIcon />
            </a>
          )}
        </div>

        <button
          onClick={handleConfirm}
          disabled={saving || currentCharId === selected.id}
          style={{
            ...S.confirmBtn,
            background: (saving || currentCharId === selected.id) ? '#555' : selected.accentColor,
            cursor: (saving || currentCharId === selected.id) ? 'default' : 'pointer',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.38s ease 0.18s, background 0.3s',
          }}
        >
          {saving ? '変更中...' : currentCharId === selected.id ? '現在のキャラクター' : 'このキャラクターに変更'}
        </button>

        {saveMessage && <div style={{ ...S.saveMsg, color: selected.accentColor }}>{saveMessage}</div>}
      </div>

      {/* 下部：フェイスアップ選択バー */}
      <div style={S.faceBar}>
        <div style={S.faceBarInner}>
          {CHARACTERS.map((char, i) => (
            <button
              key={char.id}
              onClick={() => handleSelect(i)}
              style={{
                ...S.faceBtn,
                border: i === selectedIndex ? `3px solid ${char.accentColor}` : '3px solid rgba(255,255,255,0.1)',
                boxShadow: i === selectedIndex ? `0 0 18px ${char.accentColor}99` : 'none',
                transform: i === selectedIndex ? 'scale(1.12) translateY(-5px)' : 'scale(1)',
                transition: 'all 0.25s ease',
              }}
              title={char.name}
            >
              <img src={char.faceImage} alt={char.name} style={S.faceImg} onError={e => { e.target.style.display='none'; }} />
              {currentCharId === char.id && <div style={{ ...S.faceActiveDot, background: char.accentColor }} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    position: 'relative',
    minHeight: '100vh',
    background: '#0f1117',
    color: '#fff',
    paddingTop: '60px',
    overflow: 'hidden',
    fontFamily: "'Noto Serif JP', 'Yu Mincho', 'HiraMinProN-W6', serif",
  },
  bgOverlay: {
    position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
  },
  // 立ち絵：画面全体に背景として表示
  standImageWrap: {
    position: 'fixed',
    top: '60px', left: 0, right: 0, bottom: '95px',
    zIndex: 1,
    display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
    pointerEvents: 'none',
  },
  standImage: {
    height: '100%', maxHeight: 'calc(100vh - 155px)',
    width: 'auto', maxWidth: '100%',
    objectFit: 'contain', objectPosition: 'bottom center',
    filter: 'drop-shadow(0 6px 40px rgba(0,0,0,0.7))',
  },
  // キャッチコピー縦書き：左上固定
  catchcopy: {
    position: 'fixed',
    top: '80px', left: '16px',
    zIndex: 10,
    writingMode: 'vertical-rl', textOrientation: 'mixed',
    fontSize: '16px', fontWeight: '700', letterSpacing: '0.3em', lineHeight: '1.2',
    paddingLeft: '10px', color: '#fff',
    textShadow: '0 2px 14px rgba(0,0,0,0.9)',
    whiteSpace: 'nowrap',
  },
  // 情報エリア：右下に固定
  infoArea: {
    position: 'fixed',
    right: 0, bottom: '95px',
    width: '52%', maxWidth: '280px',
    padding: '12px 12px 12px 8px',
    display: 'flex', flexDirection: 'column', gap: '10px', zIndex: 10,
  },
  charName: {
    fontSize: '20px', fontWeight: '900', letterSpacing: '0.08em',
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
  },
  activeBadge: {
    fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
    color: '#fff', fontWeight: 'bold', letterSpacing: '0.05em',
  },
  descBox: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', padding: '10px 13px',
    backdropFilter: 'blur(6px)',
  },
  descLabel: {
    fontSize: '9px', fontWeight: '800', letterSpacing: '0.22em',
    marginBottom: '5px', fontFamily: 'sans-serif',
  },
  descText: {
    fontSize: '12px', lineHeight: '1.75', color: '#ccc', margin: 0,
    fontFamily: "'Noto Sans JP', sans-serif",
  },
  snsRow: { display: 'flex', gap: '10px' },
  snsBtn: {
    width: '40px', height: '40px', borderRadius: '50%',
    border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', cursor: 'pointer', textDecoration: 'none',
    transition: 'transform 0.2s, opacity 0.2s', opacity: 0.9,
  },
  confirmBtn: {
    padding: '14px', borderRadius: '10px', border: 'none',
    color: '#fff', fontWeight: '800', fontSize: '13px', letterSpacing: '0.08em',
    fontFamily: "'Noto Sans JP', sans-serif",
  },
  saveMsg: {
    textAlign: 'center', fontWeight: 'bold', fontSize: '13px',
    fontFamily: "'Noto Sans JP', sans-serif",
  },
  faceBar: {
    position: 'fixed',
    left: '12px', bottom: '20px',
    zIndex: 20,
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
  },
  faceBarInner: {
    display: 'flex', flexDirection: 'row', gap: '10px',
    flexWrap: 'wrap', maxWidth: '180px',
  },
  faceBtn: {
    position: 'relative', width: '66px', height: '66px',
    borderRadius: '50%', background: '#1a1a2e',
    cursor: 'pointer', padding: 0, overflow: 'hidden', flexShrink: 0,
  },
  faceImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
  faceActiveDot: {
    position: 'absolute', bottom: '2px', right: '2px',
    width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #0f1117',
  },
};
